import AdminMenu from "./AdminMenu";
import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FiChevronDown, FiMail, FiCheck } from "react-icons/fi";
import MobileMenu from "./Menus/MobileMenu";
import { useAuth } from "context/auth-context";
import { useMutation } from "@tanstack/react-query";
import { toastrError, toastrSuccess } from "./../../lib/toastr";
import { subscribeUser, subscribeStatus } from "./Subscribe.api";
import { useSubscribe } from "context/Subscribe-context";
import { useTranslation } from "react-i18next";
import LogoutConfirmation from "pages/Logout/LogoutConfirmationModel";
import { BrandLogo } from "components/ui";
import { NavItem, useNavSections } from "./navConfig";

const samePath = (a: string, b?: string) =>
  !!b && a.replace(/\/+$/, "").toLowerCase() === b.toLowerCase();

function Sidebar() {
  // Submenus open automatically on their own routes; a click overrides that per menu.
  const [openOverride, setOpenOverride] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const { auth } = useAuth();
  const { isSubscribe, setisSubscribe } = useSubscribe();
  const { t } = useTranslation();
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const sections = useNavSections();

  useEffect(() => {
    const fetchInitialSubscriptionStatus = async () => {
      try {
        const response = await subscribeStatus(auth.panelistId);
        setisSubscribe(response.result);
      } catch (error) {
        setisSubscribe(0);
      }
    };
    fetchInitialSubscriptionStatus();
  }, [auth.panelistId, setisSubscribe]);

  const mutation = useMutation(() => subscribeUser(auth.panelistId), {
    onSuccess(data) {
      if (data?.result === 1) {
        setisSubscribe(data.result);
        toastrSuccess(data.message);
      }
    },
    onError() {
      toastrError(t("side.bar.subscription"));
    },
  });

  const handleClick = (e) => {
    e.preventDefault();
    mutation.mutate();
  };

  const isActive = (to?: string) => samePath(location.pathname, to);

  const toggleSubMenu = (key: string, open: boolean) => {
    setOpenOverride((current) => ({ ...current, [key]: !open }));
  };

  const renderItem = (menu: NavItem) => {
    if (menu.isAdminGroup) return <AdminMenu />;

    if (menu.isLogout) {
      return (
        <button type="button" onClick={() => setShowLogoutPopup(true)} className="zs-nav-item">
          {menu.icon}
          <span>{menu.name}</span>
        </button>
      );
    }

    if (menu.subMenus) {
      const childActive = menu.subMenus.some((sub) => isActive(sub.to));
      const open = openOverride[menu.name] ?? childActive;
      return (
        <>
          <button
            type="button"
            onClick={() => toggleSubMenu(menu.name, open)}
            className={`zs-nav-item ${childActive && !open ? "is-active" : ""}`}
            aria-expanded={open}
          >
            {menu.icon}
            <span>{menu.name}</span>
            <FiChevronDown className={`zs-nav-chevron ${open ? "is-open" : ""}`} />
          </button>
          {open && (
            <ul className="zs-nav-sub">
              {menu.subMenus.map((subMenu) => (
                <li key={subMenu.to}>
                  <NavLink to={subMenu.to} className={`zs-nav-item ${isActive(subMenu.to) ? "is-active" : ""}`}>
                    <span>{subMenu.name}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          )}
        </>
      );
    }

    return (
      <NavLink to={menu.to} className={`zs-nav-item ${isActive(menu.to) ? "is-active" : ""}`}>
        {menu.icon}
        <span className="truncate">{menu.name}</span>
      </NavLink>
    );
  };

  return (
    <>
      <aside className="zs-sidebar" aria-label="Main navigation">
        <div className="zs-sidebar-brand">
          <NavLink to="/Dashboard" aria-label="Dashboard">
            <BrandLogo className="w-auto h-8" />
          </NavLink>
        </div>

        <nav className="zs-sidebar-scroll zs-scroll">
          {sections.map((section) => (
            <div key={section.heading} className="zs-nav-section">
              <p className="zs-nav-heading">{section.heading}</p>
              <ul className="flex flex-col gap-0.5">
                {section.items.map((menu) => (
                  <li key={menu.name}>{renderItem(menu)}</li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="zs-sidebar-footer">
          <div className="p-4 border rounded-lg border-line bg-surface-2">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded bg-primary-soft text-primary-text">
                <FiMail />
              </span>
              <span className="text-[13px] font-semibold text-ink">Newsletter</span>
            </div>
            <p className="mb-3 text-xs leading-relaxed text-ink-3">{t("side.bar.straight")}</p>
            {isSubscribe === 0 ? (
              <button className="zs-btn zs-btn-primary zs-btn-sm zs-btn-block" onClick={handleClick}>
                {t("side.bar.Now")}
              </button>
            ) : (
              <button disabled={true} className="zs-btn zs-btn-soft zs-btn-sm zs-btn-block disabled:opacity-100">
                <FiCheck />
                {t("side.bar.Subscribed")}
              </button>
            )}
          </div>
        </div>
      </aside>
      <MobileMenu />
      {showLogoutPopup && <LogoutConfirmation onClose={() => setShowLogoutPopup(false)} />}
    </>
  );
}
export default Sidebar;
